import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { CriteriaColor } from '@/components/Conversations/Application/ScoreChip';
import type { Project, UserProfile } from '@factoryfixinc/ats-interfaces';
import type { MessagingSource } from '@/core/sourcing/messaging/types/messaging-source.type';
import type { ConversationIndex } from '@/core/conversations/conversation-index/conversation-index.type';
import { CopilotScoreFeedbackSubmittedSelection } from '@/core/shared/tracking/enums';
import { ResumeUploadSource } from '@/core/shared/tracking/tracking-actions';
import type { GoldUpgradeConfirmedModalProps, UpgradeToGoldModalProps } from './dialog.types';
import type {
  MarketingReportType,
  ReportCandidate,
} from '@/components/LaborMarket/labor-market-report-options';

export const useDialogStore = defineStore('dialog', () => {
  const isCreateProjectDialogOpen = ref(false);

  // Project Description Dialog
  const projectDescriptionOverlayProps = ref<{
    isOpen: boolean;
    projectId?: number;
    jobId?: number;
  }>({
    isOpen: false,
    projectId: undefined,
    jobId: undefined,
  });

  const addProToJobModalProps = ref<AddProToJobModalProps>({
    isOpen: false,
  });

  const copilotScoreFeedbackModalProps = ref<CopilotScoreFeedbackModalProps>({
    isOpen: false,
  });
  const upgradeToGoldModalProps = ref<UpgradeToGoldModalProps>({
    isOpen: false,
  });

  const goldUpgradeConfirmedModalProps = ref<GoldUpgradeConfirmedModalProps>({
    isOpen: false,
  });

  const isLaborMarketReportsModalOpen = ref(false);
  const laborMarketReportContext = ref<{
    candidates: ReportCandidate[];
    defaultType: MarketingReportType;
  } | null>(null);
  const applicantFlowInsightsSuccessModalProps = ref<
    | { isOpen: false }
    | {
        isOpen: true;
        referenceId: string;
      }
  >({ isOpen: false });

  const jobMatchReportSuccessModalProps = ref<
    | { isOpen: false }
    | {
        isOpen: true;
        referenceId: string;
      }
  >({ isOpen: false });

  const bulkMessagingModalProps = ref<BulkMessagingModalProps>({
    isOpen: false,
    conversationIndexes: [],
  });

  const uploadResumesModalProps = ref<UploadResumesModalProps>({
    isOpen: false,
  });

  // alphabetized list
  return {
    addProToJobModalProps,
    bulkMessagingModalProps,
    copilotScoreFeedbackModalProps,
    goldUpgradeConfirmedModalProps,
    isCreateProjectDialogOpen,
    isLaborMarketReportsModalOpen,
    laborMarketReportContext,
    applicantFlowInsightsSuccessModalProps,
    jobMatchReportSuccessModalProps,
    projectDescriptionOverlayProps,
    upgradeToGoldModalProps,
    uploadResumesModalProps,
  };
});

export type CopilotScoreFeedbackModalProps =
  | { isOpen: false }
  | {
      isOpen: true;
      jobApplicantId: number;
      copilotScore: number;
      copilotBlurb: string;
      feedbackType: CopilotScoreFeedbackSubmittedSelection;
      appContext?: string;
      copilotChips?: {
        title: string;
        text: string;
        color: CriteriaColor;
      }[];
      copilotChipsAsDictionary?: Record<string, string>;
    };

export type AddProToJobModalProps = {
  isOpen: boolean;
  showMessageField?: boolean;
  excludedJobIds?: number[];
  pros?: Array<Pick<UserProfile, 'id' | 'nameFirst' | 'nameLast'> & { source?: MessagingSource }>;
  additionalActionsBeforeSubmit?: (project: Project) => Promise<void>;
};

export type BulkMessagingModalProps = {
  isOpen: boolean;
  conversationIndexes: Array<
    Pick<
      ConversationIndex,
      'conversationId' | 'proId' | 'proName' | 'proEmail' | 'primaryApplicationIndex'
    >
  >;
};

export type UploadResumesModalProps =
  | { isOpen: false }
  | {
      isOpen: true;
      jobId: number;
      activeOutreachEnabled: boolean;
      source: ResumeUploadSource;
    };
